import { inject } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { UserService } from '../services/user.service';
import { Observable, of, isObservable, throwError, timer } from 'rxjs';
import { catchError, switchMap, retryWhen, mergeMap } from 'rxjs/operators';
import { UserProfile } from '../models';

export function waitForProfile(
  callback: (
    profile: UserProfile | null,
  ) => boolean | UrlTree | Observable<boolean | UrlTree>,
  forceRefresh: boolean = false,
): Observable<boolean | UrlTree> {
  const userService = inject(UserService);
  const router = inject(Router);

  const cachedProfile = userService.getCurrentUser();

  if (cachedProfile && !forceRefresh) {
    console.log('📦 Usando perfil en caché (shared)');
    const result = callback(cachedProfile);
    return isObservable(result) ? result : of(result);
  }

  console.log(
    '⏳ Esperando perfil (shared)...',
    forceRefresh ? '[FORZADO]' : '',
  );

  return userService.getUserProfile().pipe(
    // ── Reintentar solo en errores de servidor (5xx) o red (0) ──
    retryWhen((errors) =>
      errors.pipe(
        mergeMap((error, attempt) => {
          const isServerError = error.status >= 500 || error.status === 0;
          if (isServerError && attempt < 2) {
            const wait = (attempt + 1) * 1500; // 1.5s → 3s
            console.warn(
              `⚠️ Error ${error.status} (Render cold start?), reintentando en ${wait}ms... (${attempt + 1}/2)`,
            );
            return timer(wait);
          }
          return throwError(() => error);
        }),
      ),
    ),

    switchMap((profile) => {
      console.log(
        '✅ Perfil cargado (shared)',
        forceRefresh ? '[FORZADO]' : '',
      );
      const result = callback(profile);
      return isObservable(result) ? result : of(result);
    }),

    catchError((error) => {
      console.error('❌ Error cargando perfil (shared):', error);

      // Error real de autenticación → redirigir a login
      if (error.status === 401 || error.status === 403) {
        return of(router.createUrlTree(['/login']));
      }

      // Error de servidor (5xx) o red → NO tocar el token, solo bloquear navegación
      // El usuario sigue autenticado, fue un problema del servidor
      console.warn(
        '🔴 Server error en guard, bloqueando navegación sin desloguear',
      );
      return of(false);
    }),
  );
}
